import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { PaymentProvider } from '../models/payment-provider';
import { PaymentOdooService } from './payment-odoo.service';
import { environment } from '../../../../environments/environment';
import { VuPaymentProvider } from '../models/vu-payment-provider';
import { PaymentService } from './payment.service';
import { DiscountCoupon } from '../models/discount-coupon';
import { PrepaidPaymentInfo } from '../models/prepaid-payment-info';
import { GiftCard } from '../../giftcard/models/giftcard';
import { VuPaymentType } from '../models/vu-payment-type.enum';
import { VuConnectionHttpService } from '../../vu/services/vu-connection-http.service';
import { catchError, map, switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Location } from '@angular/common';
import { OrderStateService } from '../../order/services/order-state.service';
import { LoggerService } from '../../../services/logger/logger.service';
import { ShopSettingsService } from '../../general/services/shop-settings.service';
import { OrderTypeEnum } from '../../order/models/order-type.enum';
import { GiftCardStepService } from '../../giftcard/services/giftcard-step.service';
import { VuConfigurationService } from '../../vu/services/vu-configuration.service';

@Injectable()
export class PaymentVuService implements PaymentService {

  private vuPaymentCashProvider: VuPaymentProvider;
  private vuPaymentCashBillsOnlyProvider: VuPaymentProvider;
  private vuPaymentProviders: VuPaymentProvider[];

  constructor(
    private paymentOdooService: PaymentOdooService,
    private vuConnectionHttpService: VuConnectionHttpService,
    private vuConfigurationService: VuConfigurationService,
    private translateService: TranslateService,
    private location: Location,
    private orderStateService: OrderStateService,
    private loggerService: LoggerService,
    private shopSettingsService: ShopSettingsService,
    private giftCardStepService: GiftCardStepService,
  ) {
    this.vuPaymentCashProvider = new VuPaymentProvider(0, 'Cash', VuPaymentType.Cash);
    this.vuPaymentCashBillsOnlyProvider = new VuPaymentProvider(0, 'Cash', VuPaymentType.CashBillsOnly);
    this.vuPaymentProviders = [
      this.vuPaymentCashProvider,
      this.vuPaymentCashBillsOnlyProvider,
      new VuPaymentProvider(1, 'Credit Card', VuPaymentType.Card),
    ];
  }
  payOrderByGiftCard(orderId: number): Observable<boolean> {
    return this.paymentOdooService.payOrderByGiftCard(orderId);
  }

  guestPayOrderByGiftCard(orderId: number): Observable<string> {
    throw new Error('Invalid operation.');
  }

  payOrderByCash(orderId: number): Observable<boolean> {
    throw new Error('Invalid operation.');
  }

  prepaidOrder(orderNumber: number, giftCardRedeems: GiftCard[], discountCoupon: DiscountCoupon[]): Observable<PrepaidPaymentInfo> {
    return this.paymentOdooService.prepaidOrder(orderNumber, giftCardRedeems, discountCoupon);
  }

  prepaidGuestOrder(orderNumber: number, giftCardRedeems: GiftCard[], discountCoupon: DiscountCoupon[]): Observable<PrepaidPaymentInfo> {
    throw new Error('Invalid operation.');
  }

  getPaymentProviders(): Observable<PaymentProvider[]> {
    return this.vuConfigurationService.vuPaymentMethods
      .pipe(
        switchMap(paymentMethods => {
          if (this.orderStateService.order) {
            let amount = this.orderStateService.order.totalAmount;
            let extraPayAmount = 0;
            const onlyBills = paymentMethods.includes(VuPaymentType.CashBillsOnly);
            if (onlyBills && this.shopSettingsService.shopSettings.partnerCodePayment) {
              const minimalBillAmount = 5;
              extraPayAmount = minimalBillAmount - (amount % minimalBillAmount);
              if (extraPayAmount === minimalBillAmount) {
                extraPayAmount = 0;
              }
              amount += extraPayAmount;
              if (extraPayAmount === 0 && this.orderStateService.order.orderType !== OrderTypeEnum.Giftcard) {
                const giftcards = this.giftCardStepService.customGiftCardProduct;
                if (giftcards && giftcards.length !== 0) {
                  const extraPaymentProductId = giftcards[0].id;
                  const line = this.orderStateService.order.orderLines.find(x => x.productId === extraPaymentProductId);
                  if (line) {
                    extraPayAmount = line.price;
                    this.orderStateService.order.deleteOrderLine(line);
                  }
                }
              }
            }
            if (paymentMethods.includes(VuPaymentType.Cash) || onlyBills) {
              return this.vuConnectionHttpService.canAcceptCash(amount).pipe(
                map(canAcceptCash => {
                  this.vuPaymentCashProvider.disabled = !canAcceptCash;
                  this.vuPaymentCashBillsOnlyProvider.disabled = !canAcceptCash;
                  this.vuPaymentCashBillsOnlyProvider.extraPayAmount = extraPayAmount;
                  return paymentMethods;
                }),
              );
            }
          }
          return of(paymentMethods);
        }),
        map((paymentMethods) => {
          const vuPaymentProviders = this.vuPaymentProviders.filter(
            vuPaymentProvider => paymentMethods.includes(vuPaymentProvider.paymentType)
          );
          return new Array<PaymentProvider>().concat(vuPaymentProviders);
        }),
        switchMap(vuPaymentProviders => {
          return this.paymentOdooService.getV21PaymentProviders().pipe(
            map(odooVuPaymentProviders => {
              if (odooVuPaymentProviders && odooVuPaymentProviders.length !== 0) {
                return vuPaymentProviders.concat(odooVuPaymentProviders);
              }
              return vuPaymentProviders;
            }),
            catchError(error => {
              this.loggerService.info(`PaymentVuService: get V21 Payment providers failed ${error}.`);
              return of(vuPaymentProviders);
            })
          );
        })
      );
  }

  paymentTransaction(orderId: number, paymentProviderId: number): Observable<string> {
    return of(true).pipe(
      switchMap(_ => {
        const vuPaymentProvider = this.vuPaymentProviders.find(x => x.id === paymentProviderId);
        if (vuPaymentProvider) {
          if (paymentProviderId != null) {
            this.orderStateService.sessionPaymentProviderId = paymentProviderId;
            return of(this.buildHtmlForm(orderId, vuPaymentProvider.paymentType));
          }
        }
        return this.paymentOdooService.paymentTransaction(orderId, paymentProviderId);
      }),
    );
  }

  paymentGuestTransaction(orderId: number,  guestOrderKey: string, paymentProviderId: number): Observable<string> {
    throw new Error('Invalid operation.');
  }

  private buildHtmlForm(orderId: number, paymentType: VuPaymentType): string {
    return `<form action="${this.shopSettingsService.vuBaseUrl}/store/external-payment" method="get" target="_self">
        <input type="hidden" name="order_id" value="${orderId}"/>
        <input type="hidden" name="url" value="${environment.odooPaymentControllerUrl}"/>
        <input type="hidden" name="payment_type" value="${paymentType}"/>
        <input type="hidden" name="return_url" value="${this.location.prepareExternalUrl('/payment/vu-complete')}"/>
        <input type="hidden" name="cancel_url" value="${this.location.prepareExternalUrl('/payment/cancel/')}${orderId}"/>
        <input type="hidden" name="lang" value="${this.translateService.currentLang}"/>
        <button type="submit" width="100px" class="btn btn-primary"></button>
    </form>`;
  }
}
